import * as React from "react"
import type { ClipThumbnail } from "./types"

const CYCLE_INTERVAL_MS = 280

// Tracks hover state on a container and returns the index of the thumbnail to
// display: the primary while idle, then cycling through all thumbnails while
// hovered. Cleans up the interval on hover-end / unmount.
export function useThumbnailHoverCycle<E extends HTMLElement>(thumbnails: ClipThumbnail[]) {
  const ref = React.useRef<E | null>(null)
  const [hovering, setHovering] = React.useState(false)
  const [index, setIndex] = React.useState(() => primaryIndex(thumbnails))

  // Keep the resting index in sync if the thumbnail set changes.
  React.useEffect(() => {
    if (!hovering) setIndex(primaryIndex(thumbnails))
  }, [hovering, thumbnails])

  React.useEffect(() => {
    const el = ref.current
    if (!el) return
    const onEnter = () => setHovering(true)
    const onLeave = () => setHovering(false)
    el.addEventListener("mouseenter", onEnter)
    el.addEventListener("mouseleave", onLeave)
    return () => {
      el.removeEventListener("mouseenter", onEnter)
      el.removeEventListener("mouseleave", onLeave)
    }
  }, [])

  React.useEffect(() => {
    if (!hovering || thumbnails.length < 2) return
    const interval = window.setInterval(() => {
      setIndex((i) => (i + 1) % thumbnails.length)
    }, CYCLE_INTERVAL_MS)
    return () => window.clearInterval(interval)
  }, [hovering, thumbnails.length])

  return { ref, index }
}

function primaryIndex(thumbnails: ClipThumbnail[]): number {
  const idx = thumbnails.findIndex((t) => t.is_primary)
  return idx >= 0 ? idx : 0
}
